<script setup>
import { ref } from 'vue'

import IconCheckmark from '@martyrs/src/modules/icons/navigation/IconCheckmark.vue';
import IconCross from '@martyrs/src/modules/icons/navigation/IconCross.vue';

const props = defineProps({
  submit: {
    type: Function,
    default: async () => { console.log('Button click.') }
  },
  text: {
    type: Object,
    default: () => ({
      success: null,
      error: null
    })
  },
  counter: {
    type: Object
  },
  callback: {
    type: Function,
    default: async () => { console.log('Button callback.') }
  },
  callbackDelay: {
    type: Number,
    default: 750
  },
  showSucces: {
    type: Boolean,
    default: true
  },
  showLoader: {
    type: Boolean,
    default: true
  },
  validation: {
    type: Boolean,
    default: false
  },
})

const emits = defineEmits(['error'])

const button = ref(null)
const error = ref(null)
const loading = ref(false)
const finished = ref(false)

async function Submit() {
  console.log('click')
  
  button.value.style['pointer-events'] = 'none'
  error.value = null
  loading.value = true

  // Функция для сброса состояния кнопки
  const resetButton = () => {
    if (button.value) {
      button.value.style.pointerEvents = 'auto'
      loading.value = false
      finished.value = false
      error.value = null
    }
  }

  try {
    await props.submit()

    button.value.classList.replace('bg-main', 'bg-second')
    loading.value = false

    // Используем функцию сброса состояния кнопки здесь
    if (props.showSucces) { 
      finished.value = true
      setTimeout(() => {
        resetButton()
        button.value.classList.replace('bg-second', 'bg-main')
      }, 500)
    } else {
      resetButton()
      button.value.classList.replace('bg-second', 'bg-main')
    }

    // Если есть callback, мы также установим таймер для его вызова
    if (props.callback) setTimeout(() => props.callback(), props.callbackDelay)

  } catch (err) {
    console.error('=== BUTTON ERROR ===', err)
    emits('error', err)
    // Если возникла ошибка, мы изменяем стили и устанавливаем сообщение об ошибке
    button.value.classList.replace('bg-main', 'bg-fourth-nano')
    loading.value = false
    error.value = true
    
    // После задержки снова сбрасываем состояние кнопки
    setTimeout(() => {
      resetButton()
      // Так как класс кнопки был изменен, вернем его в исходное состояние
      button.value.classList.replace('bg-fourth-nano', 'bg-main')
    }, 1330)
  }
}
</script>

<template>
  <button 
    @click.stop="Submit"
    :disabled="validation"
    ref="button"
		class="button"
    :class="{ 'button--disabled': loading || validation }"
  >
    <span class="button-content" :class="{ 'hidden': loading || error || finished }">
      <slot></slot>
    </span>

    <span class="button-loader" :class="{ 'active': loading && showLoader }">
      <span class="dot"></span>
      <span class="dot"></span>
      <span class="dot"></span>
    </span>

    <span class="button-success" :class="{ 'active': finished && showSucces }">
      <template v-if="text.success">{{ text.success }}</template>
      <IconCheckmark v-else class="i-regular" />
    </span>

    <span class="button-error" :class="{ 'active': error }">
      <template v-if="text.error">{{ text.error }}</template>
      <IconCross v-else class="i-regular" />
    </span>
    
    <!-- Counter -->
    <div v-if="counter" class="button-counter font-second flex flex-center">
      <span class="t-black dark:t-white">{{ counter }}</span>
    </div>
  </button>
</template>

<style lang="scss">
button[disabled] {
  opacity: 0.75 !important;
  pointer-events: none !important;
  cursor: default !important;
  color: rgba(var(--dark), 0.33) !important;
  background: rgba(var(--light), 1) !important;
}

a.button {
  text-box: trim-both cap alphabetic;
}

.button {
  position: relative;
  display: flex;
  padding: var(--small);
  border-radius: var(--small);
  text-box: trim-both cap alphabetic;
  transform: scale(1);
  opacity: 1;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 1rem;
  transition: all 0.33s ease;

  &:hover {
    cursor: pointer;
    opacity: 0.9;
  }

  &:active {
    transform: scale(0.95);
  }

  &-small {
    padding: 0.75rem;
    border-radius: 0.5rem;
    height: fit-content;
  }

  .button-counter {
    position: absolute;
    right: 0px;
    bottom: 0px;
    background: yellow;
    height: 16px;
    border-radius: 16px;
    width: 16px;
    font-weight: 500;
    text-align: center;
    line-height: 16px;
    font-size: 10px;
  }
}

.button-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transform: translateY(0);
  opacity: 1;
  transition:
    opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &.hidden {
    opacity: 0;
    transform: translateY(4px);
  }
}

.button-loader {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  opacity: 0;
  transform: translateY(4px);
  transition:
    opacity 0.25s cubic-bezier(0.0, 0, 0.2, 1) 0.05s,
    transform 0.25s cubic-bezier(0.0, 0, 0.2, 1) 0.05s;
  pointer-events: none;

  &.active {
    opacity: 1;
    transform: translateY(0);
  }

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    animation: dot-bounce 0.8s cubic-bezier(0.4, 0, 0.2, 1) infinite;

    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: 0.12s; }
    &:nth-child(3) { animation-delay: 0.24s; }
  }
}

@keyframes dot-bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

.button-success {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transform: translateY(4px);
  transition:
    opacity 0.2s cubic-bezier(0.0, 0, 0.2, 1),
    transform 0.2s cubic-bezier(0.0, 0, 0.2, 1);
  pointer-events: none;

  &.active {
    opacity: 1;
    transform: translateY(0);
  }
}

.button-error {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transform: translateY(4px);
  transition:
    opacity 0.2s cubic-bezier(0.0, 0, 0.2, 1),
    transform 0.2s cubic-bezier(0.0, 0, 0.2, 1);
  pointer-events: none;

  &.active {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>